import { CdkDragEnd } from '@angular/cdk/drag-drop';
import { Component, Input, Output, EventEmitter } from '@angular/core';
import { PDFDocument, rgb, StandardFonts, degrees } from 'pdf-lib';
import { MessageService } from 'primeng/api';

@Component({
    selector: 'app-pdf-editor',
    templateUrl: './pdf-editor.component.html',
    styleUrls: ['./pdf-editor.component.scss'],
    providers: [MessageService],
    standalone: false
})
export class PdfEditorComponent {
  @Input() pdfSrc: string | Uint8Array = '';
  @Output() onComplete = new EventEmitter<string>();
  @Output() onCancel = new EventEmitter<void>();

  annotations: any[] = [];
  selectedIndex: number | null = null;
  private idCounter = 0;

  // Start at A4 height so ng2-pdf-viewer's offsetParent visibility check passes
  // before the first page is rendered. Updated to actual height after render.
  pdfViewerHeight: number = 842;

  // Per-page rotation storage: { pageIndex: degrees }
  pageRotations: { [key: number]: number } = {};
  isCurrentPageLandscape: boolean = false;
  hasLandscapePages: boolean = false;

  constructor(private messageService: MessageService) {}

  get selectedItem() {
    return this.selectedIndex !== null
      ? this.annotations[this.selectedIndex]
      : null;
  }

  updateSelected(patch: Partial<any>) {
    if (this.selectedIndex === null) return;
    this.annotations[this.selectedIndex] = {
      ...this.annotations[this.selectedIndex],
      ...patch,
    };
  }

  addText() {
    const viewport = document.querySelector('.canvas-viewport') as HTMLElement;
    const spawnY = viewport ? viewport.scrollTop + 100 : 50;
    this.annotations.push({
      id: ++this.idCounter,
      type: 'text',
      value: 'Enter Text',
      x: 50,
      y: spawnY,
      fontSize: 14,
    });
    this.selectedIndex = this.annotations.length - 1;
  }

  onPageRendered() {
    const scrollWrapper = document.querySelector(
      '.pdf-scroll-wrapper',
    ) as HTMLElement;
    const annotationLayer = document.querySelector(
      '.annotation-layer',
    ) as HTMLElement;
    if (scrollWrapper && annotationLayer) {
      annotationLayer.style.width = scrollWrapper.offsetWidth + 'px';
      annotationLayer.style.height = scrollWrapper.offsetHeight + 'px';
    }
    setTimeout(() => {
    this.checkCurrentPageOrientation();
  }, 100);
  }

  rotatePdf() {
    const container = document.getElementById('pdf-container');
    const pageNodes = document.querySelectorAll('.pdf-viewer-container .page');
    if (!container || pageNodes.length === 0) return;

    let targetIndex = 0;
    let minDiff = Number.MAX_VALUE;

    // Find the page closest to the top of the scroll viewport
    pageNodes.forEach((node, index) => {
      const rect = node.getBoundingClientRect();
      const diff = Math.abs(rect.top);
      if (diff < minDiff) {
        minDiff = diff;
        targetIndex = index;
      }
    });

    this.pageRotations[targetIndex] =
      ((this.pageRotations[targetIndex] || 0) + 90) % 360;

    const targetPageEl = pageNodes[targetIndex] as HTMLElement;
    const isLandscape = this.pageRotations[targetIndex] % 180 !== 0;

    targetPageEl.style.transform = `rotate(${this.pageRotations[targetIndex]}deg)`;

    if (isLandscape) {
      const extraSpace = Math.abs(targetPageEl.offsetHeight - targetPageEl.offsetWidth) / 2;
      targetPageEl.style.setProperty('margin-top',    `${extraSpace}px`, 'important');
      targetPageEl.style.setProperty('margin-bottom', `${extraSpace}px`, 'important');
      targetPageEl.style.setProperty('margin-left',   '0',               'important');
      targetPageEl.style.setProperty('margin-right',  '0',               'important');
    } else {
      targetPageEl.style.setProperty('margin-top',    '0',     'important');
      targetPageEl.style.setProperty('margin-bottom', '25px',  'important');
      targetPageEl.style.setProperty('margin-left',   '0',     'important');
      targetPageEl.style.setProperty('margin-right',  '0',     'important');
    }

    // CRITICAL: Force the annotation layer to resize after the CSS transition
    setTimeout(() => {
      this.onPageRendered();
      this.checkCurrentPageOrientation();
    }, 500);
  }

  private updateHasLandscapePages(): void {
    const pageNodes = document.querySelectorAll('.pdf-viewer-container .page');
    if (pageNodes.length === 0) {
      this.hasLandscapePages = false;
      return;
    }
    let found = false;
    pageNodes.forEach((node, index) => {
      const el = node as HTMLElement;
      const rot = this.pageRotations[index] || 0;
      const isSwapped = rot === 90 || rot === 270;
      const effectiveWidth  = isSwapped ? el.offsetHeight : el.offsetWidth;
      const effectiveHeight = isSwapped ? el.offsetWidth  : el.offsetHeight;
      if (effectiveWidth > effectiveHeight) found = true;
    });
    this.hasLandscapePages = found;
  }

  rotateAllLandscape() {
    const pageNodes = document.querySelectorAll('.pdf-viewer-container .page');
    if (pageNodes.length === 0) return;

    let changesMade = false;

    pageNodes.forEach((node, index) => {
      const targetPageEl = node as HTMLElement; 
      const currentRot = this.pageRotations[index] || 0;
      const isSwapped = currentRot === 90 || currentRot === 270;
      const width = isSwapped ? targetPageEl.offsetHeight : targetPageEl.offsetWidth;
      const height = isSwapped ? targetPageEl.offsetWidth : targetPageEl.offsetHeight;
     
      if (width > height) {
        const newRotation = (currentRot + 90) % 360;
        this.pageRotations[index] = newRotation;
        
        targetPageEl.style.transform = `rotate(${newRotation}deg)`;

        const isNowSwapped = newRotation === 90 || newRotation === 270;

        if (isNowSwapped) {
          const extraSpace = Math.abs(targetPageEl.offsetHeight - targetPageEl.offsetWidth) / 2;
          targetPageEl.style.setProperty('margin-top',    `${extraSpace}px`, 'important');
          targetPageEl.style.setProperty('margin-bottom', `${extraSpace}px`, 'important');
          targetPageEl.style.setProperty('margin-left',   '0',               'important');
          targetPageEl.style.setProperty('margin-right',  '0',               'important');
        } else {
          targetPageEl.style.setProperty('margin-top',    '0',    'important');
          targetPageEl.style.setProperty('margin-bottom', '25px', 'important');
          targetPageEl.style.setProperty('margin-left',   '0',    'important');
          targetPageEl.style.setProperty('margin-right',  '0',    'important');
        }
        changesMade = true;
      }
    });

    if (changesMade) {
      setTimeout(() => {
        this.onPageRendered();
        this.checkCurrentPageOrientation();
      }, 500);
      
      this.messageService.add({
        severity: 'success', 
        summary: 'Success', 
        detail: 'All landscape pages rotated to portrait'
      });
    }
  }


  trackById(index: number, item: any): number {
    return item.id;
  }

  async onLogoUpload(event: any) {
    const file = event.target.files[0];
    if (!file) return;
    const viewport = document.querySelector('.canvas-viewport') as HTMLElement;
    const spawnY = viewport ? viewport.scrollTop + 100 : 100;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.annotations.push({
        id: ++this.idCounter,
        type: 'image',
        value: e.target.result,
        x: 100,
        y: spawnY,
        width: 120,
      });
      this.selectedIndex = this.annotations.length - 1;
    };
    reader.readAsDataURL(file);
  }

  selectItem(index: number) {
    this.selectedIndex = index;
  }

  deselect(event: MouseEvent) {
    if ((event.target as HTMLElement).classList.contains('canvas-viewport')) {
      this.selectedIndex = null;
    }
  }

  removeSelected() {
    if (this.selectedIndex !== null) {
      this.annotations = this.annotations.filter(
        (_, i) => i !== this.selectedIndex,
      );
      this.selectedIndex = null;
    }
  }

  onDragEnd(event: CdkDragEnd, index: number) {
    const pos = event.source.getFreeDragPosition();
    this.annotations[index].x = pos.x;
    this.annotations[index].y = pos.y;
  }

  checkCurrentPageOrientation() {
  const container = document.getElementById('pdf-container');
  const pageNodes = document.querySelectorAll('.pdf-viewer-container .page');
  if (!container || pageNodes.length === 0) return;

  let targetIndex = 0;
  let minDiff = Number.MAX_VALUE;
  const containerTop = container.getBoundingClientRect().top;

  // Find the page currently at the top of the viewport
  pageNodes.forEach((node, index) => {
    const rect = node.getBoundingClientRect();
    const diff = Math.abs(rect.top - containerTop);
    if (diff < minDiff) {
      minDiff = diff;
      targetIndex = index;
    }
  });

  const activePageEl = pageNodes[targetIndex] as HTMLElement;
  const currentRot = this.pageRotations[targetIndex] || 0;
  
  // If rotated 90 or 270 degrees, the physical width and height swap visually
  const isSwapped = currentRot === 90 || currentRot === 270;

  const effectiveWidth = isSwapped ? activePageEl.offsetHeight : activePageEl.offsetWidth;
  const effectiveHeight = isSwapped ? activePageEl.offsetWidth : activePageEl.offsetHeight;

  // It is landscape if the width is greater than the height
  this.isCurrentPageLandscape = effectiveWidth > effectiveHeight;
  this.updateHasLandscapePages();
}

  async finalizePdf() {
    const emptyText = this.annotations.find(
      (item) => item.type === 'text' && !item.value.trim(),
    );
    if (emptyText) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Text cannot be empty.',
      });
      return;
    }

    try {
      this.messageService.add({
        severity: 'info',
        summary: 'Processing',
        detail: 'Finalizing PDF...',
      });

      let existingPdfBytes: ArrayBuffer | Uint8Array;
      if (this.pdfSrc instanceof Uint8Array) {
        existingPdfBytes = this.pdfSrc;
      } else if (typeof this.pdfSrc === 'string' && this.pdfSrc.startsWith('data:')) {
        // base64 data URI — decode directly, do not use fetch()
        const base64 = this.pdfSrc.split(',')[1];
        existingPdfBytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
      } else {
        existingPdfBytes = await fetch(this.pdfSrc as string).then((res) => res.arrayBuffer());
      }
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const pages = pdfDoc.getPages();
      const pageViewNodes = document.querySelectorAll(
        '.pdf-viewer-container .page',
      );
      const annotationLayer = document.querySelector(
        '.annotation-layer',
      ) as HTMLElement;
      const layerRect = annotationLayer.getBoundingClientRect();

      // 1. APPLY ROTATIONS FIRST (WITHOUT CROPPING THE PAGE)
      Object.keys(this.pageRotations).forEach((key) => {
        const idx = parseInt(key);
        const rotation = this.pageRotations[idx];
        const page = pages[idx];

        if (page && rotation !== 0) {
          // We ONLY rotate the orientation metadata. No page.setSize() = NO CROPPING!
          const currentRotation = page.getRotation().angle;
          page.setRotation(degrees((currentRotation + rotation) % 360));
        }
      });

      // 2. DRAW ANNOTATIONS USING GEOMETRIC CENTERS
      for (const item of this.annotations) {
        let targetPageIndex = 0;
        let minDistance = Number.MAX_VALUE;
        let centerX = 0;
        let centerY = 0;

        // Find the specific page the user dropped the item on
        for (let i = 0; i < pageViewNodes.length; i++) {
          const pageEl = pageViewNodes[i] as HTMLElement;
          const rect = pageEl.getBoundingClientRect();

          // Calculate the center of the visual page relative to the annotation layer
          const pageCenterX = rect.left - layerRect.left + rect.width / 2;
          const pageCenterY = rect.top - layerRect.top + rect.height / 2;

          // Find which page center is closest to the dropped item
          const dist = Math.sqrt(
            Math.pow(item.x - pageCenterX, 2) +
              Math.pow(item.y - pageCenterY, 2),
          );
          if (dist < minDistance) {
            minDistance = dist;
            targetPageIndex = i;
            centerX = pageCenterX;
            centerY = pageCenterY;
          }
        }

        const targetPage = pages[targetPageIndex];
        const pageEl = pageViewNodes[targetPageIndex] as HTMLElement;
        const cssRot = this.pageRotations[targetPageIndex] || 0;

        // Calculate distance from the item to the center of the page
        const dx = item.x - centerX;
        const dy = item.y - centerY;

        // Un-rotate the coordinates so they match the original PDF layout
        const rad = -(cssRot * Math.PI) / 180;
        const dx_unrotated = dx * Math.cos(rad) - dy * Math.sin(rad);
        const dy_unrotated = dx * Math.sin(rad) + dy * Math.cos(rad);

        // Map to the top-left of the original unrotated page element
        const unrotatedX = pageEl.offsetWidth / 2 + dx_unrotated;
        const unrotatedY = pageEl.offsetHeight / 2 + dy_unrotated;

        // Map to PDF sizing
        const pdfWidth = targetPage.getWidth(); // Original unrotated width
        const pdfHeight = targetPage.getHeight(); // Original unrotated height

        const sX = pdfWidth / pageEl.offsetWidth;
        const sY = pdfHeight / pageEl.offsetHeight;

        let pdfX = unrotatedX * sX;
        let pdfY = pdfHeight - unrotatedY * sY;

        // Draw the items, rotating them backwards so they appear upright on the rotated page
        if (item.type === 'text') {
          // Adjust anchor points based on rotation
          let drawX = pdfX;
          let drawY = pdfY;
          if (cssRot === 0) drawY -= item.fontSize * sY;
          else if (cssRot === 90) drawX += item.fontSize * sX;
          else if (cssRot === 180) drawY += item.fontSize * sY;
          else if (cssRot === 270) drawX -= item.fontSize * sX;

          targetPage.drawText(item.value, {
            x: drawX,
            y: drawY,
            size: item.fontSize * sX,
            font: helveticaFont,
            color: rgb(0, 0, 0),
            rotate: degrees(-cssRot),
          });
        } else if (item.type === 'image') {
          const base64Data = item.value.split(',')[1];
          const bytes = Uint8Array.from(atob(base64Data), (c) =>
            c.charCodeAt(0),
          );
          const embeddedImage = item.value.includes('image/png')
            ? await pdfDoc.embedPng(bytes)
            : await pdfDoc.embedJpg(bytes);

          const pdfImgWidth = item.width * sX;
          const pdfImgHeight =
            pdfImgWidth * (embeddedImage.height / embeddedImage.width);

          let drawX = pdfX;
          let drawY = pdfY;
          if (cssRot === 0) drawY -= pdfImgHeight;
          else if (cssRot === 90) drawX += pdfImgHeight;
          else if (cssRot === 180) {
            drawX += pdfImgWidth;
            drawY += pdfImgHeight;
          } else if (cssRot === 270) drawY -= pdfImgWidth;

          targetPage.drawImage(embeddedImage, {
            x: drawX,
            y: drawY,
            width: pdfImgWidth,
            height: pdfImgHeight,
            rotate: degrees(-cssRot),
          });
        }
      }

      const modifiedBase64 = await pdfDoc.saveAsBase64({ dataUri: true });
      this.onComplete.emit(modifiedBase64);
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'PDF Saved',
      });
    } catch (error) {
      console.error('Finalize PDF Error:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Process failed',
      });
    }
  }
}
